import { useState, useEffect } from 'react';
import { Appointment, AppointmentFormData, Patient, Fisioterapeuta } from '../types/crud/appointments';
import { citasApi, pacientesApi, profesionalesApi } from '../services/api';
import type { Paciente, Profesional, CitaFilters } from '../types/api';

// Función para mapear paciente de API a nuestro tipo
const mapPatientFromApi = (paciente: Paciente): Patient => ({
  id: paciente.id.toString(),
  firstName: paciente.nombre,
  lastName: paciente.apellido || paciente.apellidos || '',
  email: paciente.email,
  phone: paciente.telefono,
  dateOfBirth: paciente.fecha_nacimiento
});

// Función para mapear profesional de API a nuestro tipo
const mapFisioterapeutaFromApi = (profesional: Profesional): Fisioterapeuta => ({
  id: profesional.id.toString(),
  firstName: profesional.nombre,
  lastName: profesional.apellido || profesional.apellidos || '',
  specialty: profesional.especialidad?.nombre || 'Sin especialidad',
  email: profesional.email
});

// Función para mapear cita de API a nuestro tipo
const mapAppointmentFromApi = (cita: any, pacientes: Patient[], fisioterapeutas: Fisioterapeuta[]): Appointment => {
  // La API usa id_paciente e id_profesional en lugar de paciente_id y profesional_id
  const pacienteId = cita.id_paciente || cita.paciente_id;
  const profesionalId = cita.id_profesional || cita.profesional_id;

  const paciente = pacientes.find(p => p.id === pacienteId?.toString());
  const fisioterapeuta = fisioterapeutas.find(f => f.id === profesionalId?.toString());

  // Combinar fecha y hora para crear los objetos Date
  let startDate: Date = new Date();
  let endDate: Date = new Date();

  try {
    const fecha = cita.fecha; // YYYY-MM-DD
    const horaInicio = cita.hora_inicio; // HH:MM:SS
    const horaFin = cita.hora_fin; // HH:MM:SS
    if (fecha && horaInicio) {
      startDate = new Date(`${fecha}T${horaInicio}`);
    }
    if (fecha && horaFin) {
      endDate = new Date(`${fecha}T${horaFin}`);
    } else if (fecha && horaInicio) {
      // Si no hay horaFin, sumar 1 hora a horaInicio
      const temp = new Date(`${fecha}T${horaInicio}`);
      endDate = new Date(temp.getTime() + 60 * 60 * 1000);
    }
    // Validar que las fechas son válidas
    if (isNaN(startDate.getTime())) startDate = new Date();
    if (isNaN(endDate.getTime())) endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
  } catch (error) {
    startDate = new Date();
    endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
  }

  // Generar título basado en paciente y tratamiento
  const pacienteNombre = cita.paciente ? 
    `${cita.paciente.nombre} ${cita.paciente.apellidos || ''}` : 
    `${paciente?.firstName || 'Paciente'} ${paciente?.lastName || ''}`;
  const tratamientoNombre = cita.tratamiento?.nombre || '';
  const title = `${pacienteNombre.trim()}${tratamientoNombre ? ` - ${tratamientoNombre}` : ''}`;

  const status = mapStatusFromApi(cita.estado);

  const appointment: Appointment = {
    id: cita.id.toString(),
    title,
    start: startDate,
    end: endDate,
    patientId: pacienteId?.toString() || '',
    patient: paciente,
    fisioterapeutaId: profesionalId?.toString() || '',
    fisioterapeuta,
    description: cita.notas || cita.observaciones || '',
    status,
    type: 'evaluation', // Por defecto
    notes: cita.notas || cita.observaciones || '',
    backgroundColor: getStatusColor(status, isDarkMode()),
    borderColor: getStatusColor(status, isDarkMode()),
    textColor: getStatusTextColor(status, isDarkMode())
  };
  return appointment;
};

// Mapear estados de la API a nuestros tipos
const mapStatusFromApi = (estado: string): Appointment['status'] => {
  switch (estado.toUpperCase()) {
    case 'PENDIENTE': return 'PENDIENTE';
    case 'CONFIRMADA': return 'CONFIRMADA';
    case 'REALIZADA': return 'REALIZADA';
    case 'CANCELADA': return 'CANCELADA';
    case 'NO_ACUDE': return 'NO_ACUDE';
    case 'REPROGRAMADA': return 'REPROGRAMADA';
    default: return 'PENDIENTE';
  }
};

// Mapear nuestros estados a la API
const mapStatusToApi = (status: Appointment['status']): 'PENDIENTE' | 'CONFIRMADA' | 'REALIZADA' | 'CANCELADA' | 'NO_ACUDE' | 'REPROGRAMADA' => {
  switch (status) {
    case 'PENDIENTE': return 'PENDIENTE';
    case 'CONFIRMADA': return 'CONFIRMADA';
    case 'REALIZADA': return 'REALIZADA';
    case 'CANCELADA': return 'CANCELADA';
    case 'NO_ACUDE': return 'NO_ACUDE';
    case 'REPROGRAMADA': return 'REPROGRAMADA';
    default: return 'PENDIENTE';
  }
};

// Función para obtener color por estado
const getStatusColor = (status: Appointment['status'], isDark: boolean = false): string => {
  const colorMapDark: Record<Appointment['status'], string> = {
    PENDIENTE: '#fbbf24', // amber-400
    CONFIRMADA: '#60a5fa', // blue-400
    REALIZADA: '#34d399', // emerald-400
    CANCELADA: '#f87171', // red-400
    NO_ACUDE: '#f87171', // red-400
    REPROGRAMADA: '#fbbf24', // amber-400
  };
  const colorMapLight: Record<Appointment['status'], string> = {
    PENDIENTE: '#d97706', // amber-600
    CONFIRMADA: '#2563eb', // blue-600
    REALIZADA: '#059669', // emerald-600
    CANCELADA: '#dc2626', // red-600
    NO_ACUDE: '#dc2626', // red-600
    REPROGRAMADA: '#d97706', // amber-600
  };
  return isDark ? colorMapDark[status] : colorMapLight[status];
};

// Función para obtener color de texto por estado
const getStatusTextColor = (status: Appointment['status'], isDark: boolean = false): string => {
  // En modo oscuro, usamos texto más claro para mejor contraste
  // En modo claro, usamos texto blanco para mejor contraste sobre colores oscuros
  return isDark ? '#1f2937' : '#ffffff'; // gray-800 para dark mode, white para light mode
};

// Función para detectar si estamos en modo oscuro
const isDarkMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return document.documentElement.classList.contains('dark') || 
         window.matchMedia('(prefers-color-scheme: dark)').matches;
};

export const useAppointments = (filters?: { profesional_id?: string; tratamiento_id?: string }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [fisioterapeutas, setFisioterapeutas] = useState<Fisioterapeuta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTheme, setCurrentTheme] = useState<boolean>(() => isDarkMode());

  // Efecto para actualizar colores cuando cambia el tema
  useEffect(() => {
    const checkThemeChange = () => {
      const newTheme = isDarkMode();
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
        setAppointments(prevAppointments => 
          prevAppointments.map(appointment => ({
            ...appointment,
            backgroundColor: getStatusColor(appointment.status, newTheme),
            borderColor: getStatusColor(appointment.status, newTheme),
            textColor: getStatusTextColor(appointment.status, newTheme)
          }))
        );
      }
    };
    const observer = new MutationObserver(checkThemeChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkThemeChange);
    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', checkThemeChange);
    };
  }, [currentTheme]);

  // Cargar datos iniciales y cuando cambian los filtros
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        let citaFilters: CitaFilters = {};
        if (filters) {
          if (filters.profesional_id) citaFilters.profesional_id = Number(filters.profesional_id);
          if (filters.tratamiento_id) citaFilters.tratamiento_id = Number(filters.tratamiento_id);
        }
        const [pacientesData, profesionalesData, citasData] = await Promise.all([
          pacientesApi.getAll(),
          profesionalesApi.getAll(),
          citasApi.getAll(citaFilters)
        ]);
        const mappedPatients = Array.isArray(pacientesData?.data?.pacientes)
          ? pacientesData.data.pacientes.map(mapPatientFromApi)
          : (Array.isArray(pacientesData) ? pacientesData.map(mapPatientFromApi) : []);
        const mappedFisioterapeutas = Array.isArray(profesionalesData?.data?.profesionales)
          ? profesionalesData.data.profesionales.map(mapFisioterapeutaFromApi)
          : (Array.isArray(profesionalesData) ? profesionalesData.map(mapFisioterapeutaFromApi) : []);
        setPatients(mappedPatients);
        setFisioterapeutas(mappedFisioterapeutas);
        let citasFiltradas: any[] = [];
        if (typeof citasData === 'object' && citasData !== null && 'data' in citasData && Array.isArray((citasData as any).data?.citas)) {
          citasFiltradas = (citasData as any).data.citas;
        } else if (Array.isArray(citasData)) {
          citasFiltradas = citasData;
        }
        if (filters && (filters as any).paciente_id) {
          citasFiltradas = citasFiltradas.filter((cita: any) => String(cita.id_paciente || cita.paciente_id) === String((filters as any).paciente_id));
        }
        let mappedAppointments: Appointment[] = [];
        try {
          if (Array.isArray(citasFiltradas)) {
            mappedAppointments = citasFiltradas.map((cita: any) => 
              mapAppointmentFromApi(cita, mappedPatients, mappedFisioterapeutas)
            );
            setAppointments(mappedAppointments);
          } else {
            setAppointments([]);
          }
        } catch (error) {
          setAppointments([]);
        }
      } catch (error) {
        setPatients([]);
        setFisioterapeutas([]);
        setAppointments([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [filters]);

  const createAppointment = async (appointmentData: AppointmentFormData): Promise<Appointment> => {
    try {
      // Preparar datos para la API
      const fechaCita = new Date(appointmentData.start);
      const horaInicio = appointmentData.start.split('T')[1] || '09:00';
      const horaFin = appointmentData.end.split('T')[1] || '10:00';
      
      // Usar any para el tipo ya que la API real usa campos diferentes a los definidos en el tipo Cita
      const citaData: any = {
        id_paciente: parseInt(appointmentData.patientId),
        id_profesional: parseInt(appointmentData.fisioterapeutaId),
        id_tratamiento: 1, // Por defecto, ya que no tenemos selector de tratamiento aún
        fecha: fechaCita.toISOString().split('T')[0], // YYYY-MM-DD
        hora_inicio: horaInicio,
        hora_fin: horaFin,
        estado: mapStatusToApi(appointmentData.status),
        notas: appointmentData.description || ''
      };

      // Crear en la API
      const newCita = await citasApi.create(citaData);
      
      // Mapear la respuesta
      const patient = patients.find(p => p.id === appointmentData.patientId);
      const fisioterapeuta = fisioterapeutas.find(f => f.id === appointmentData.fisioterapeutaId);
      
      const newAppointment: Appointment = {
        id: newCita.id.toString(),
        title: appointmentData.title,
        start: new Date(appointmentData.start),
        end: new Date(appointmentData.end),
        patientId: appointmentData.patientId,
        patient,
        fisioterapeutaId: appointmentData.fisioterapeutaId,
        fisioterapeuta,
        description: appointmentData.description,
        status: appointmentData.status,
        type: appointmentData.type,
        notes: appointmentData.notes,
        backgroundColor: getStatusColor(appointmentData.status, currentTheme),
        borderColor: getStatusColor(appointmentData.status, currentTheme),
        textColor: getStatusTextColor(appointmentData.status, currentTheme)
      };

      setAppointments(prev => [...prev, newAppointment]);
      return newAppointment;
    } catch (error) {
      console.error('Error al crear cita:', error);
      throw error;
    }
  };

  const updateAppointment = async (id: string, appointmentData: Partial<AppointmentFormData>): Promise<Appointment> => {
    try {
      const existingAppointment = appointments.find(apt => apt.id === id);
      if (!existingAppointment) {
        throw new Error('Cita no encontrada');
      }

      // Preparar datos para actualizar
      const updateData: any = {};
      
      if (appointmentData.start) {
        const fechaCita = new Date(appointmentData.start);
        updateData.fecha = fechaCita.toISOString().split('T')[0];
        updateData.hora_inicio = appointmentData.start.split('T')[1] || existingAppointment.start.toString().split('T')[1];
      }
      
      if (appointmentData.end) {
        updateData.hora_fin = appointmentData.end.split('T')[1] || existingAppointment.end.toString().split('T')[1];
      }
      
      if (appointmentData.patientId) {
        updateData.id_paciente = parseInt(appointmentData.patientId);
      }
      
      if (appointmentData.fisioterapeutaId) {
        updateData.id_profesional = parseInt(appointmentData.fisioterapeutaId);
      }
      
      if (appointmentData.status) {
        updateData.estado = mapStatusToApi(appointmentData.status);
      }
      
      if (appointmentData.description !== undefined) {
        updateData.notas = appointmentData.description;
      }

      // Actualizar en la API
      await citasApi.update(parseInt(id), updateData);
      
      // Mapear la respuesta actualizada
      const patient = appointmentData.patientId 
        ? patients.find(p => p.id === appointmentData.patientId) 
        : existingAppointment.patient;
      
      const fisioterapeuta = appointmentData.fisioterapeutaId 
        ? fisioterapeutas.find(f => f.id === appointmentData.fisioterapeutaId) 
        : existingAppointment.fisioterapeuta;

      const updatedAppointment: Appointment = {
        ...existingAppointment,
        ...appointmentData,
        start: appointmentData.start ? new Date(appointmentData.start) : existingAppointment.start,
        end: appointmentData.end ? new Date(appointmentData.end) : existingAppointment.end,
        patient,
        fisioterapeuta,
        backgroundColor: appointmentData.status 
          ? getStatusColor(appointmentData.status, currentTheme) 
          : existingAppointment.backgroundColor,
        borderColor: appointmentData.status 
          ? getStatusColor(appointmentData.status, currentTheme) 
          : existingAppointment.borderColor,
        textColor: appointmentData.status 
          ? getStatusTextColor(appointmentData.status, currentTheme) 
          : existingAppointment.textColor
      };

      setAppointments(prev => prev.map(apt => apt.id === id ? updatedAppointment : apt));
      return updatedAppointment;
    } catch (error) {
      console.error('Error al actualizar cita:', error);
      throw error;
    }
  };

  const deleteAppointment = async (id: string): Promise<void> => {
    try {
      // Cancelar la cita en lugar de eliminarla
      await citasApi.cancelar(parseInt(id), 'Cancelada por el usuario');
      
      // Actualizar el estado local
      setAppointments(prev => prev.map(apt => 
        apt.id === id 
          ? { ...apt, status: 'CANCELADA', backgroundColor: getStatusColor('CANCELADA', currentTheme), borderColor: getStatusColor('CANCELADA', currentTheme) }
          : apt
      ));
    } catch (error) {
      console.error('Error al cancelar cita:', error);
      throw error;
    }
  };

  return {
    appointments,
    patients,
    fisioterapeutas,
    isLoading,
    createAppointment,
    updateAppointment,
    deleteAppointment,
    getStatusColor: (status: Appointment['status']) => getStatusColor(status, currentTheme),
    getStatusTextColor: (status: Appointment['status']) => getStatusTextColor(status, currentTheme),
    isDarkMode: currentTheme
  };
}; 